import { createPlatformClient, type PlatformClient } from '@sfc/sdk';

/**
 * The dashboard's one door to the backend.
 *
 * It goes through the published SDK, which knows only HTTP and the generated
 * contract. Nothing here imports NestJS, Drizzle or any other server code, so
 * the dashboard cannot grow a shortcut into the database.
 */
const configuredBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://127.0.0.1:8787';

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

/**
 * Calls the API under the same loopback name the page was opened with.
 *
 * `localhost` and `127.0.0.1` are different sites to a browser, so a session
 * cookie set for one is not sent to the other, and `SameSite=Lax` withholds it
 * on the cross-site request the dashboard makes. Whichever name is in the
 * address bar, signing in would then appear to work and the next session check
 * would drop it. A deployment names its real hosts, so this only ever adjusts
 * a loopback pair and leaves everything else alone.
 */
function resolveBaseUrl(raw: string): string {
  try {
    const api = new URL(raw);
    const page = window.location.hostname;
    if (LOOPBACK_HOSTS.has(api.hostname) && LOOPBACK_HOSTS.has(page) && api.hostname !== page) {
      api.hostname = page;
    }
    return api.origin;
  } catch {
    return raw;
  }
}

export const platformBaseUrl = resolveBaseUrl(configuredBaseUrl);
export const platform: PlatformClient = createPlatformClient({ baseUrl: platformBaseUrl, timeoutMs: 5000 });
